import { getCurrentScope, onScopeDispose } from 'vue';

export type SaveFailure = 'retry' | 'fatal';

export interface ProgressSaveQueueOptions<T> {
  /** Sends one payload; rejects on failure. */
  send: (payload: T) => Promise<void>;
  /**
   * Called on every failed send. Return 'fatal' to drop the queue without
   * retrying (e.g. premium access denied); anything else retries with backoff.
   */
  onError?: (error: unknown) => SaveFailure | void;
  /** Backoff before retry n (1-based): base * 2^(n-1), capped. Default 1000 ms. */
  retryBaseMs?: number;
  retryMaxMs?: number;
  /** Retries per payload before it is dropped (the next save carries full state anyway). */
  maxRetries?: number;
  /** A send that has not settled after this long counts as failed. Default 15 s. */
  timeoutMs?: number;
  /**
   * Called once when a payload is given up on for good: a 'fatal' verdict, or
   * its retries ran out with no newer payload waiting. Not called for failures
   * while draining or after the scope is disposed.
   */
  onDrop?: (error: unknown) => void;
}

/**
 * A client error the server will answer the same way on every retry:
 * 4xx except 408 (timeout) and 429 (rate limit).
 */
export function isFatalSaveError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return (
    typeof status === 'number' &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
}

/** Default cap for drain(): a hung save must not hold the caller forever. */
export const DRAIN_MAX_WAIT_MS = 10_000;

export class SaveTimeoutError extends Error {
  constructor(ms: number) {
    super(`progress save timed out after ${ms} ms`);
    this.name = 'SaveTimeoutError';
  }
}

/**
 * Serialises progress saves: at most one request in flight; while one is in
 * flight, newer payloads coalesce into a single pending one (the LATEST wins,
 * since every payload carries the full progress); failures retry with backoff.
 */
export function useProgressSaveQueue<T>(options: ProgressSaveQueueOptions<T>) {
  const {
    send,
    onError,
    retryBaseMs = 1000,
    retryMaxMs = 30_000,
    maxRetries = 5,
    timeoutMs = 15_000,
    onDrop,
  } = options;

  let pending: { payload: T } | null = null;
  let inFlight = false;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let draining = 0;
  let disposed = false;
  let idleWaiters: Array<() => void> = [];

  function isIdle() {
    return !inFlight && !pending && !retryTimer;
  }

  function notifyIdle() {
    if (!isIdle()) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  function clearRetry() {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  }

  function pump() {
    if (inFlight || retryTimer || !pending) {
      notifyIdle();
      return;
    }
    const current = pending;
    pending = null;
    let request: Promise<void>;
    try {
      request = send(current.payload);
    } catch (error) {
      request = Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
    inFlight = true;
    // Settles once: on the response, or as a failure after timeoutMs.
    let settled = false;
    const timeout = setTimeout(
      () => onFailure(new SaveTimeoutError(timeoutMs)),
      timeoutMs,
    );
    const onSuccess = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      attempt = 0;
      inFlight = false;
      pump();
    };
    const onFailure = (error: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      inFlight = false;
      const verdict = onError?.(error);
      if (verdict === 'fatal') {
        cancel();
        if (!draining && !disposed) onDrop?.(error);
        return;
      }
      // A newer payload supersedes the failed one; otherwise resend it.
      const superseded = !!pending;
      if (!superseded) pending = current;
      attempt++;
      if (draining || disposed) {
        // Last chance already taken: nothing is retried.
        attempt = 0;
        pending = null;
        notifyIdle();
        return;
      }
      if (attempt > maxRetries) {
        attempt = 0;
        if (!superseded) {
          pending = null;
          onDrop?.(error);
        }
        pump();
        return;
      }
      const delay = Math.min(retryBaseMs * 2 ** (attempt - 1), retryMaxMs);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        pump();
      }, delay);
    };
    request.then(onSuccess, onFailure);
  }

  /** Queue `payload` as the latest state: sent now when idle, else after the in-flight one. */
  function enqueue(payload: T) {
    pending = { payload };
    pump();
  }

  /** Send anything waiting for a retry now (e.g. when the view goes away). */
  function flush() {
    if (retryTimer) {
      clearRetry();
      pump();
    }
  }

  /**
   * Resolves once nothing is in flight or pending, or after `maxWaitMs`
   * (whichever comes first). Waiting retries are sent at once and failures
   * while draining are not retried.
   */
  function drain(maxWaitMs = DRAIN_MAX_WAIT_MS): Promise<void> {
    draining++;
    flush();
    pump();
    let cap: ReturnType<typeof setTimeout> | undefined;
    return new Promise<void>((resolve) => {
      cap = setTimeout(() => {
        idleWaiters = idleWaiters.filter((w) => w !== resolve);
        resolve();
      }, maxWaitMs);
      idleWaiters.push(resolve);
      notifyIdle();
    }).finally(() => {
      clearTimeout(cap);
      draining--;
    });
  }

  /** Drop the pending payload and any waiting retry (an in-flight request still settles). */
  function cancel() {
    pending = null;
    attempt = 0;
    clearRetry();
    notifyIdle();
  }

  // Going away: send a waiting retry one last time; nothing is retried after.
  if (getCurrentScope())
    onScopeDispose(() => {
      disposed = true;
      flush();
    });

  return {
    enqueue,
    drain,
    cancel,
    flush,
    hasPending: () => !!pending,
    isIdle,
  };
}
